import { useMsal } from '@azure/msal-react'
import { useBreakpointValue } from '@chakra-ui/react'
import {
    mdiAccountLockOutline,
    mdiCalendarCheck,
    mdiCogOutline,
    mdiFileCogOutline,
    mdiHandCoinOutline,
    mdiHistory,
    mdiLogout,
    mdiMenu,
    mdiMessageCogOutline,
    mdiMessageOutline,
    mdiMoonWaningCrescent,
    mdiWhiteBalanceSunny,
} from '@mdi/js'
import Icon from '@mdi/react'
import { useMatchRoute, useNavigate } from '@tanstack/react-router'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'

import HomeIcon from '@/components/assets/HomeIcon'
import NotificationBadge from '@/components/notificationBadge'
import { Box } from '@/components/ui/box'
import { IconButton } from '@/components/ui/button'
import { MenuContent, MenuItem, MenuItemGroup, MenuRoot, MenuSeparator, MenuTrigger } from '@/components/ui/menu'
import { LINKS } from '@/constants'
import { useColorMode } from '@/hooks/useColorMode'
import useColorModeValue from '@/hooks/useColorModeValue'
import { toggleUploadMobileDrawer } from '@/store/gigiChat/ChatAtom'
import { toggleQuotasDrawer } from '@/store/gigiChat/Quotas'
import {
    toggleChatHistoryMobileDrawer,
    toggleChatSettingsMobileDrawer,
    toggleDocumentsHistoryMobileDrawer,
    toggleTranslateHistoryMobileDrawer,
} from '@/store/mobile/MobileAtom'
import { approvalsTotalAtom, compliancesTotalAtom } from '@/store/tasksApprovals'

import HelpCenterMobileVersion from '../helpCenter/mobile'

const HeaderMenu = () => {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { toggleColorMode } = useColorMode()
    const { accounts, instance } = useMsal()
    const user = accounts[0]
    const matchRoute = useMatchRoute()
    const [tasksTotal] = useAtom(compliancesTotalAtom)
    const [approvalsTotal] = useAtom(approvalsTotalAtom)

    const logout = () => {
        void instance.logout()
    }

    const menuIcon = useBreakpointValue(
        {
            base: <Icon path={mdiMenu} size={1} />,
            lg: (
                <Icon
                    path={mdiCogOutline}
                    size="20px"
                    color={useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)')}
                />
            ),
        },
        { ssr: false },
    )
    return (
        <MenuRoot variant="profile" closeOnSelect={false}>
            <MenuTrigger aria-label="Profile menu" data-id="Profile menu" asChild>
                <IconButton variant="menu" icon={menuIcon} />
            </MenuTrigger>
            <MenuContent>
                <Box display={['block', 'block', 'block', 'none']}>
                    {matchRoute({ to: '/chat' }) && (
                        <>
                            <MenuItem value={t('global.header.chatSettings')} onClick={toggleChatSettingsMobileDrawer} closeOnSelect={true}>
                                <Icon path={mdiMessageCogOutline} size={1} />
                                {t('global.header.chatSettings')}
                            </MenuItem>
                            <MenuItem value={t('global.header.history')} onClick={toggleChatHistoryMobileDrawer} closeOnSelect={true}>
                                <Icon path={mdiHistory} size={1} />
                                {t('global.header.history')}
                            </MenuItem>
                            <MenuSeparator />
                        </>
                    )}

                    {matchRoute({ to: '/translate' }) && (
                        <>
                            <MenuItem
                                value={t('global.header.history')}
                                mt="8px"
                                onClick={toggleTranslateHistoryMobileDrawer}
                                closeOnSelect={true}
                            >
                                <Icon path={mdiHistory} size={1} />
                                {t('global.header.history')}
                            </MenuItem>
                            <MenuItem value={t('global.header.translate')} mt="8px" onClick={toggleUploadMobileDrawer} closeOnSelect={true}>
                                <Icon path={mdiFileCogOutline} size={1} />
                                {t('global.header.translate')}
                            </MenuItem>
                            <MenuSeparator />
                        </>
                    )}
                    {matchRoute({ to: '/documents' }) && (
                        <>
                            <MenuItem
                                value={t('global.header.history')}
                                mt="8px"
                                onClick={toggleDocumentsHistoryMobileDrawer}
                                closeOnSelect={true}
                            >
                                <Icon path={mdiHistory} size={1} />
                                {t('global.header.history')}
                            </MenuItem>
                            <MenuItem
                                value={t('global.header.manageDocuments')}
                                mt="8px"
                                onClick={toggleUploadMobileDrawer}
                                closeOnSelect={true}
                            >
                                <Icon path={mdiFileCogOutline} size={1} />
                                {t('global.header.manageDocuments')}
                            </MenuItem>
                            <MenuSeparator />
                        </>
                    )}

                    <MenuItemGroup title={t('global.header.modes')}></MenuItemGroup>
                    {
                        <MenuItem value={t('global.header.routes.main')} onClick={() => void navigate({ to: '/' })} closeOnSelect={true}>
                            <HomeIcon color={useColorModeValue('var(--chakra-colors-dark-700)', 'var(--chakra-colors-white)')} />
                            {t('global.header.routes.main')}
                        </MenuItem>
                    }
                    {!matchRoute({ to: '/chat' }) && (
                        <MenuItem
                            value={t('global.header.routes.chat')}
                            onClick={() => void navigate({ to: '/chat' })}
                            closeOnSelect={true}
                        >
                            <Icon path={mdiMessageOutline} size={1} />
                            {t('global.header.routes.chat')}
                        </MenuItem>
                    )}
                    {!matchRoute({ to: '/translate' }) && (
                        <MenuItem
                            value={t('global.header.routes.translate')}
                            onClick={() => void navigate({ to: '/translate' })}
                            closeOnSelect={true}
                        >
                            <Icon path={mdiCalendarCheck} size={1} />
                            {t('global.header.routes.translate')}
                        </MenuItem>
                    )}
                    {!matchRoute({ to: '/documents' }) && (
                        <MenuItem
                            value={t('global.header.routes.documents')}
                            onClick={() => void navigate({ to: '/documents' })}
                            closeOnSelect={true}
                        >
                            <Icon path={mdiCalendarCheck} size={1} />
                            {t('global.header.routes.documents')}
                        </MenuItem>
                    )}
                    {!matchRoute({ to: '/tasks-approvals' }) && (
                        <MenuItem
                            value={t('global.header.routes.tasksApprovals')}
                            onClick={() => void navigate({ to: '/tasks-approvals' })}
                            closeOnSelect={true}
                        >
                            <Icon path={mdiCalendarCheck} size={1} />
                            {t('global.header.routes.tasksApprovals')}
                            <NotificationBadge total={tasksTotal + approvalsTotal} />
                        </MenuItem>
                    )}
                    <MenuSeparator />
                </Box>

                <HelpCenterMobileVersion />

                <MenuItemGroup title={user.name}></MenuItemGroup>
                <MenuItem value={t('global.quotas.mainMenu')} onClick={toggleQuotasDrawer} closeOnSelect={true} data-id="quota-open">
                    <Icon path={mdiHandCoinOutline} size={1} />
                    {t('global.quotas.mainMenu')}
                </MenuItem>
                <MenuItem
                    value={useColorModeValue(t('global.header.theme.dark'), t('global.header.theme.light'))}
                    onClick={toggleColorMode}
                    closeOnSelect={false}
                >
                    <Icon path={useColorModeValue(mdiMoonWaningCrescent, mdiWhiteBalanceSunny)} size={1} />
                    {useColorModeValue(t('global.header.theme.dark'), t('global.header.theme.light'))}
                </MenuItem>
                <MenuItem
                    as="a"
                    value={t('global.header.privacy')}
                    closeOnSelect={true}
                    // @ts-expect-error -- a needs href to work
                    href={LINKS.PRIVACY}
                    target="_blank"
                    rel="noreferrer"
                >
                    <Icon path={mdiAccountLockOutline} size={1} />

                    {t('global.header.privacy')}
                </MenuItem>
                <MenuItem value={t('global.header.logout')} onClick={logout} className="logout" closeOnSelect={true}>
                    <Icon path={mdiLogout} size={1} color="var(--chakra-colors-status-error" />
                    {t('global.header.logout')}
                </MenuItem>
                <MenuItemGroup title="Version: 1.6.2"></MenuItemGroup>
            </MenuContent>
        </MenuRoot>
    )
}

export default HeaderMenu
